import every from 'lodash/every';
import filter from 'lodash/filter';
import flatMap from 'lodash/flatMap';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import keys from 'lodash/keys';
import map from 'lodash/map';
import some from 'lodash/some';
import sum from 'lodash/sum';

import constants from '@/constants';

import DateTimeUtil from '@/util/date-time-util';
import WorkScheduleUtil from '@/util/work-schedule-util';

const getters = {

  approvals: (state) => map(keys(state.approvalsByUsername), (username) => state.approvalsByUsername[username]),

  getApprovalsByUsername: (state) => (username) => state.approvalsByUsername[username],

  getSelectedTimePairs: (state) => flatMap(map(
    keys(state.approvalsByUsername),
    (username) => filter(
      state.approvalsByUsername[username].summaries,
      (timePairSummary) => state.timePairSelectability[timePairSummary.id].selected,
    ),
  )),

  getSelectableApprovals: (state) => flatMap(map(
    keys(state.approvalsByUsername),
    (username) => filter(
      state.approvalsByUsername[username].summaries,
      (timePairSummary) => state.timePairSelectability[timePairSummary.id].currentlySelectable,
    ),
  )),

  getSelectableApprovalsByUsername: (state) => (username) => filter(
    state.approvalsByUsername[username].summaries,
    (timePairSummary) => state.timePairSelectability[timePairSummary.id].currentlySelectable,
  ),

  getFilteredTimes: (state) => (times) => {
    if (isEmpty(state.activeFilters)) {
      return times;
    }
    return filter(times, (time) => every(
      map(state.activeFilters, (filterContainer) => {
        switch (filterContainer.filteredAttribute) {
          case 'Position':
            return includes(filterContainer.selectedFilters, time.employeeAssignment.position.description);
          case 'Clock In':
            return some(
              map(
                filterContainer.selectedFilters, (selection) => includes(
                  DateTimeUtil.convertTo12HourHumanReadableDateTime(time.clockIn.date), selection,
                ),
              ),
            );
          case 'Clock Out':
            if (!time.clockOut) return false;

            return some(
              map(
                filterContainer.selectedFilters, (selection) => includes(
                  DateTimeUtil.convertTo12HourHumanReadableDateTime(time.clockOut.date), selection,
                ),
              ),
            );
          case 'Type':
            return some(
              map(filterContainer.selectedFilters, (selection) => selection === time.type),
            );
          case 'Work Schedule Deviation':
            return some(
              map(
                filterContainer.selectedFilters,
                (selection) => WorkScheduleUtil.doesWorkScheduleFilterApply(selection, time),
              ),
            );
          case 'Duration':
          default: return times;
        }
      }),
    ));
  },

  getWorkScheduleDayWorkingMinutes: (state) => (timePair) => {
    const expectedWorkScheduleDays = WorkScheduleUtil.getExpectedWorkScheduleDays(
      state,
      timePair.startingWorkScheduleDay,
      WorkScheduleUtil.getSpanningDays(timePair),
      timePair.workSchedule,
    );

    return sum(map(expectedWorkScheduleDays, (day) => day.workingMinutes));
  },

  getWorkScheduleByExternalCode: (state) => (externalCode) => state.workSchedulesByExternalCode[externalCode],

  getDailyWorkScheduleByEmployee: (state) => (employee) => state.dailyWorkScheduleByEmployee[employee],

  getAssignedWorkScheduleRanges: (state) => (assignmentId) => (assignmentId in state.assignedWorkScheduleRanges
    ? state.assignedWorkScheduleRanges[assignmentId]
    : []),

  getEmployeeSchedules: (state) => () => (state.employeeSchedules),

  getNoLocationDataRequired: () => () => localStorage.getItem(constants.LocalStorage.NO_LOCATION_DATA) === 'true',

  getFeatureSwitch: (state) => (featureName) => state.featureSwitches[featureName] === true,

  getFeatureSchedule: (state) => (featureName) => state.featureSchedules[featureName],

  getTheme: (state) => () => state.theme,
};

export default getters;
